export type EvidenceActivityStep =
  | "opened"
  | "draft_created"
  | "generate"
  | "field_edited"
  | "section_toggled"
  | "fact_toggled"
  | "conclusion_rewritten"
  | "screenshot_added"
  | "screenshot_removed"
  | "pdf_downloaded"
  | "package_downloaded"
  | "abandoned"
  | "submitted";

export type EvidenceActivityBucket = "screenshots" | "reviewing" | "fields" | "rewrites";

export const EVIDENCE_ACTIVITY_PROCESSORS = ["shopify", "paypal", "stripe", "adyen", "bank"] as const;
export type EvidenceActivityProcessor = typeof EVIDENCE_ACTIVITY_PROCESSORS[number];

export const EVIDENCE_ACTIVITY_OUTCOMES = ["finished", "downloaded", "open", "abandoned"] as const;
export type EvidenceActivityOutcome = typeof EVIDENCE_ACTIVITY_OUTCOMES[number];

export type EvidenceActivityResult = "won" | "lost" | null;

export const EVIDENCE_ACTIVITY_RESULT_FILTERS = ["won", "lost", "awaiting"] as const;
export type EvidenceActivityResultFilter = typeof EVIDENCE_ACTIVITY_RESULT_FILTERS[number];

export const EVIDENCE_ACTIVITY_SORTS = ["time", "finishedAt", "agent", "dispute", "processor", "outcome", "result"] as const;
export type EvidenceActivitySort = typeof EVIDENCE_ACTIVITY_SORTS[number];
export type EvidenceActivityDir = "asc" | "desc";

export const SLOW_GAP_SECONDS = 120;

export interface EvidenceActivityEvent {
  at: string;
  step: EvidenceActivityStep;
  detail: Record<string, unknown> | null;
  userId: number;
  gapSeconds: number | null;
  counted: boolean;
}

export interface EvidenceActivitySession {
  startedAt: string;
  endedAt: string;
  timeSeconds: number;
  idleBeforeSeconds: number | null;
  events: EvidenceActivityEvent[];
}

export interface EvidenceActivityTrail {
  summary: { timeSeconds: number; steps: number; generates: number };
  users: { user_id: number; name: string }[];
  sessions: EvidenceActivitySession[];
}

export interface EvidenceActivityAgentRow {
  userId: number;
  name: string;
  finished: number;
  abandoned: number;
  downloaded: number;
  won: number;
  lost: number;
  medianSeconds: number;
  p90Seconds: number;
  perHour: number;
  generatesPerPackage: number;
  screenshotsPerPackage: number;
  rewritesPerPackage: number;
  timeSeconds: number;
}

export interface EvidenceActivityPackageRow {
  draftId: number;
  disputeId: number | null;
  bankCaseId: number | null;
  orderName: string | null;
  companyName: string | null;
  processor: EvidenceActivityProcessor;
  agent: string;
  people: number;
  finishedAt: string | null;
  result: EvidenceActivityResult;
  timeSeconds: number;
  outcome: EvidenceActivityOutcome;
}

export interface EvidenceActivitySummary {
  tiles: {
    finished: number;
    downloaded: number;
    open: number;
    abandoned: number;
    medianSeconds: number;
    p90Seconds: number;
    totalSeconds: number;
    won: number;
    lost: number;
    awaiting: number;
  };
  agents: EvidenceActivityAgentRow[];
  total: EvidenceActivityAgentRow;
  buckets: { bucket: EvidenceActivityBucket; medianSeconds: number; share: number }[];
  packages: EvidenceActivityPackageRow[];
  packagesTotal: number;
  page: number;
  pageSize: number;
  agentOptions: { user_id: number; name: string }[];
}
